import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = {
  // Auth
  login: (email, password) => axios.post(`${API}/auth/login`, { email, password }),
  register: (data) => axios.post(`${API}/auth/register`, data),
  getMe: () => axios.get(`${API}/auth/me`),

  // Leads
  getLeads: (params) => axios.get(`${API}/leads`, { params }),
  getLead: (id) => axios.get(`${API}/leads/${id}`),
  createLead: (data) => axios.post(`${API}/leads`, data),
  updateLead: (id, data) => axios.put(`${API}/leads/${id}`, data),
  updateLeadDetails: (id, data) => axios.put(`${API}/leads/${id}/details`, data),
  deleteLead: (id) => axios.delete(`${API}/leads/${id}`),
  assignLead: (leadId, operatorId) => axios.post(`${API}/leads/${leadId}/assign`, null, { params: { operator_id: operatorId } }),
  transferLeadToMe: (leadId) => axios.post(`${API}/leads/${leadId}/transfer-to-me`),
  importLeads: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/leads/import`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  exportLeads: () => axios.get(`${API}/leads/export/excel`, { responseType: 'blob' }),

  // Teams
  getTeams: () => axios.get(`${API}/teams`),
  createTeam: (data) => axios.post(`${API}/teams`, data),
  deleteTeam: (teamId) => axios.delete(`${API}/teams/${teamId}`),
  updateTeam: (teamId, data) => axios.put(`${API}/teams/${teamId}`, data),
  addOperatorToTeam: (teamId, operatorId) => axios.post(`${API}/teams/${teamId}/operators`, { operator_id: operatorId }),
  removeOperatorFromTeam: (teamId, operatorId) => axios.delete(`${API}/teams/${teamId}/operators/${operatorId}`),
  getTeamPerformance: (teamId, period) => axios.get(`${API}/teams/${teamId}/performance`, { params: { period } }),
  getTeamsComparison: (period) => axios.get(`${API}/teams/performance/comparison`, { params: { period } }),
  getDistributionReport: (params) => axios.get(`${API}/teams/report/distribution`, { params }),

  // Operators
  getOperators: () => axios.get(`${API}/operators`),
  createOperator: (data) => axios.post(`${API}/operators`, data),
  deleteOperator: (id) => axios.delete(`${API}/operators/${id}`),
  toggleOperator: (id) => axios.put(`${API}/operators/${id}/toggle`),
  pauseOperator: (id) => axios.put(`${API}/operators/${id}/pause`),
  sendHeartbeat: () => axios.post(`${API}/operators/heartbeat`),
  getOnlineStatus: () => axios.get(`${API}/operators/online-status`),

  // Tags
  getTags: () => axios.get(`${API}/tags`),
  createTag: (data) => axios.post(`${API}/tags`, data),
  deleteTag: (id) => axios.delete(`${API}/tags/${id}`),

  // Products
  getProducts: () => axios.get(`${API}/products`),
  createProduct: (data) => axios.post(`${API}/products`, data),
  updateProduct: (id, data) => axios.put(`${API}/products/${id}`, data),
  deleteProduct: (id) => axios.delete(`${API}/products/${id}`),

  // Sales
  getSales: (params = {}) => axios.get(`${API}/sales`, { params }),
  createSale: (data) => axios.post(`${API}/sales`, data),
  applyDiscount: (saleId, amount) => axios.put(`${API}/sales/${saleId}/discount`, null, { params: { amount } }),
  approveSale: (saleId, observation) => axios.put(`${API}/sales/${saleId}/approve`, { observation }),
  rejectSale: (saleId, observation) => axios.put(`${API}/sales/${saleId}/reject`, { observation }),
  editSale: (saleId, payload) => axios.put(`${API}/sales/${saleId}`, payload),
  deleteSale: (saleId) => axios.delete(`${API}/sales/${saleId}`),

  // Notes (internal)
  getNotes: (leadId) => axios.get(`${API}/notes/${leadId}`),
  createNote: (data) => axios.post(`${API}/notes`, data),

  // Timeline
  getTimeline: (leadId) => axios.get(`${API}/timeline/${leadId}`),

  // Tracking
  addTracking: (data) => axios.post(`${API}/tracking`, data),
  getTrackings: (leadId) => axios.get(`${API}/tracking/${leadId}`),
  deleteTracking: (id) => axios.delete(`${API}/tracking/${id}`),
  getTrackingStatus: (code) => axios.get(`${API}/tracking/status/${code}`),

  // Billing & Pipeline
  getPipelineStages: () => axios.get(`${API}/billing/stages`),
  getPipelineView: () => axios.get(`${API}/billing/pipeline`),
  updateLeadStage: (leadId, stage) => axios.post(`${API}/billing/stage`, { lead_id: leadId, stage }),
  submitCreditAnalysis: (data) => axios.post(`${API}/billing/credit-analysis`, data),
  getCreditAnalysis: (leadId) => axios.get(`${API}/billing/credit-analysis/${leadId}`),
  createInstallments: (data) => axios.post(`${API}/billing/installments`, data),
  getInstallments: (leadId) => axios.get(`${API}/billing/installments/${leadId}`),
  markInstallmentPaid: (id) => axios.put(`${API}/billing/installments/${id}/pay`),
  markInstallmentOverdue: (id) => axios.put(`${API}/billing/installments/${id}/overdue`),

  // Dashboard
  getDashboardMetrics: (params) => axios.get(`${API}/dashboard/metrics`, { params }),
  getLeadsByStatus: () => axios.get(`${API}/dashboard/leads-by-status`),
  getLeadsBySource: () => axios.get(`${API}/dashboard/leads-by-source`),
  getDailyLeads: (days = 30) => axios.get(`${API}/dashboard/daily-leads`, { params: { days } }),
  getSalesMetrics: () => axios.get(`${API}/dashboard/sales-metrics`),

  // WhatsApp
  sendMessage: (data) => axios.post(`${API}/whatsapp/send`, data),
  getMessages: (leadId) => axios.get(`${API}/whatsapp/messages/${leadId}`),

  // AI Assistant
  transcribeAudio: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/ai/transcribe`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  generateAIResponse: (leadId, message, includeAudio = true) =>
    axios.post(`${API}/ai/respond`, { lead_id: leadId, message }, { params: { include_audio: includeAudio } }),
  processAudioMessage: (leadId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/ai/process-audio`, formData, { params: { lead_id: leadId }, headers: { 'Content-Type': 'multipart/form-data' } });
  },
  textToSpeech: (text, voice = 'nova') =>
    axios.post(`${API}/ai/text-to-speech`, null, { params: { text, voice }, responseType: 'blob' }),
  getAISettings: () => axios.get(`${API}/ai/settings`),
  updateAISettings: (settings) => axios.put(`${API}/ai/settings`, settings),

  // Evolution API (WhatsApp)
  getEvolutionConfig: () => axios.get(`${API}/evolution/config`),
  getEvolutionConfigSecondary: () => axios.get(`${API}/evolution/config/secondary`),
  saveEvolutionConfig: (config) => axios.post(`${API}/evolution/config`, config),
  saveEvolutionConfigSecondary: (config) => axios.post(`${API}/evolution/config/secondary`, config),
  getEvolutionQRCode: (instanceName) => axios.get(`${API}/evolution/instance/qrcode/${instanceName}`),
  getEvolutionStatus: (instanceName) => axios.get(`${API}/evolution/instance/status/${instanceName}`),
  logoutEvolution: (instanceName) => axios.post(`${API}/evolution/instance/logout/${instanceName}`),
  sendWhatsAppText: (phone, message, leadId) =>
    axios.post(`${API}/evolution/send/text`, { phone, message, lead_id: leadId }),
  sendWhatsAppMedia: (phone, mediaUrl, caption, mediaType) =>
    axios.post(`${API}/evolution/send/media`, { phone, media_url: mediaUrl, caption, media_type: mediaType }),
  createEvolutionInstance: (instanceName) => axios.post(`${API}/evolution/instance/create`, null, { params: { instance_name: instanceName } }),
  setupEvolutionWebhook: (webhookUrl, instanceName) => axios.post(`${API}/evolution/webhook/setup`, null, { params: { webhook_url: webhookUrl, instance_name: instanceName || undefined } }),

  // All Instances
  getAllInstances: () => axios.get(`${API}/evolution/all-instances`),
  addInstanceMapping: (instanceName, product) => axios.post(`${API}/evolution/all-instances/add`, { instance_name: instanceName, product }),
  removeInstanceMapping: (instanceName) => axios.delete(`${API}/evolution/all-instances/${instanceName}`),
  getInstanceProducts: () => axios.get(`${API}/evolution/instance-products`),

  // Notifications
  getNewLeads: (since) => axios.get(`${API}/notifications/new-leads`, { params: { since } }),
  getNewMessages: (since) => axios.get(`${API}/notifications/new-messages`, { params: { since } }),
  getCallbacks: () => axios.get(`${API}/notifications/callbacks`),
  markCallbackDone: (callbackId) => axios.post(`${API}/notifications/callbacks/${callbackId}/done`),
  getDisconnectionAlerts: () => axios.get(`${API}/notifications/disconnection-alerts`),
  acknowledgeDisconnectionAlert: (alertId) => axios.post(`${API}/notifications/disconnection-alerts/${alertId}/acknowledge`),

  // Auth
  changePassword: (newPassword) => axios.post(`${API}/auth/change-password`, { new_password: newPassword }),

  // Lead actions
  markCallAttempted: (leadId) => axios.post(`${API}/leads/${leadId}/call-attempted`),
  updateCallStatus: (leadId, status) => axios.post(`${API}/leads/${leadId}/call-status`, { status }),

  // WhatsApp Protection
  getProtectionSettings: () => axios.get(`${API}/whatsapp-protection/settings`),
  updateProtectionSettings: (settings) => axios.put(`${API}/whatsapp-protection/settings`, settings),
  resetWarmup: () => axios.post(`${API}/whatsapp-protection/reset-warmup`),

  // Media
  getMedia: (messageId) => axios.get(`${API}/whatsapp/media/${messageId}`),
  getMediaDebug: (messageId) => axios.get(`${API}/whatsapp/media-debug/${messageId}`),

  // Mark messages as read
  markRead: (leadId) => axios.post(`${API}/whatsapp/mark-read/${leadId}`),

  // Send audio
  sendAudio: (leadId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/whatsapp/send-audio/${leadId}`, formData);
  },

  // Send media (image, video, document)
  sendMediaFile: (leadId, file, caption) => {
    const formData = new FormData();
    formData.append('file', file);
    if (caption) formData.append('caption', caption);
    return axios.post(`${API}/whatsapp/send-media/${leadId}`, formData);
  },

  // Webhook Logs
  getWebhookLogs: (params) => axios.get(`${API}/webhook-logs`, { params }),
  getWebhookStats: (days) => axios.get(`${API}/webhook-logs/stats`, { params: { days } }),
  getDisconnectionHistory: (days) => axios.get(`${API}/webhook-logs/disconnections`, { params: { days } }),

  // WhatsApp Recovery (anti-drop)
  getRecoveryHealth: () => axios.get(`${API}/whatsapp-recovery/health`),
  syncRecoveryNow: (instance_name, hours = 2) => axios.post(`${API}/whatsapp-recovery/sync-now`, { instance_name, hours }),
  restartInstance: (name) => axios.post(`${API}/whatsapp-recovery/restart-instance/${name}`),
  importByPhone: (phone, name) => axios.post(`${API}/whatsapp-recovery/import-by-phone`, { phone, name }),
  searchWebhookLogsByPhone: (phone, days = 7) => axios.get(`${API}/whatsapp-recovery/search-webhook-logs`, { params: { phone, days } }),
  getRestartLogs: (days = 7) => axios.get(`${API}/whatsapp-recovery/restart-logs`, { params: { days } }),

  // Smart Reengagement
  getReengagementStats: (days = 7) => axios.get(`${API}/reengagement/stats`, { params: { days } }),
  getReengagementCandidates: () => axios.get(`${API}/reengagement/candidates`),
  runReengagementNow: () => axios.post(`${API}/reengagement/run-now`),
  getReengagementFunnel: (days = 7, filter = 'all') => axios.get(`${API}/reengagement/funnel`, { params: { days, filter } }),
};
